import React, { useState, useEffect, useMemo } from "react";
import { useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Edit2, Trash2, Mail, Phone, Briefcase, AlertCircle, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const Staff = () => {
  const { t, language } = useLanguage();
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    is_active: true,
    service_ids: [],
  });

  useEffect(() => {
    fetchStaff();
    fetchServices();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaff(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, { withCredentials: true });
      // back-compat: treat undefined is_active as active
      setServices((response.data || []).filter(s => s.is_active !== false));
    } catch {
      /* silent · keep empty list */
    }
  };

  const servicesById = useMemo(() => {
    const m = {};
    services.forEach(s => { m[s.service_id] = s; });
    return m;
  }, [services]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingStaff) {
        await axios.patch(`${API}/staff/${editingStaff.staff_id}`, formData, {
          withCredentials: true
        });
        toast.success(language === "it" ? "Operatore aggiornato" : "Staff updated");
      } else {
        await axios.post(`${API}/staff`, formData, { withCredentials: true });
        toast.success(language === "it" ? "Operatore creato" : "Staff created");
      }

      setDialogOpen(false);
      setEditingStaff(null);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      email: member.email || "",
      phone: member.phone || "",
      is_active: member.is_active,
      service_ids: Array.isArray(member.service_ids) ? [...member.service_ids] : [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm(language === "it" ? "Eliminare questo operatore?" : "Delete this staff member?")) {
      return;
    }

    try {
      await axios.delete(`${API}/staff/${staffId}`, { withCredentials: true });
      toast.success(language === "it" ? "Operatore eliminato" : "Staff deleted");
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      is_active: true,
      service_ids: [],
    });
  };

  const toggleService = (serviceId) => {
    setFormData(f => {
      const has = f.service_ids.includes(serviceId);
      return {
        ...f,
        service_ids: has
          ? f.service_ids.filter(id => id !== serviceId)
          : [...f.service_ids, serviceId],
      };
    });
  };

  const selectAllServices = () => {
    setFormData(f => ({ ...f, service_ids: services.map(s => s.service_id) }));
  };

  const clearAllServices = () => {
    setFormData(f => ({ ...f, service_ids: [] }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif font-medium">{t("staff")}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" ? "Gestisci il team del tuo salone" : "Manage your salon team"}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingStaff(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-lift" data-testid="add-staff-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t("add")} {language === "it" ? "operatore" : "staff"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingStaff
                    ? (language === "it" ? "Modifica Operatore" : "Edit Staff")
                    : (language === "it" ? "Nuovo Operatore" : "New Staff")
                  }
                </DialogTitle>
                <DialogDescription>
                  {language === "it"
                    ? "Anagrafica, contatti e servizi che questo operatore può eseguire."
                    : "Profile, contacts and services this staff member can perform."}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("name")} *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={language === "it" ? "Nome operatore" : "Staff name"}
                    required
                    data-testid="staff-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{t("email")}</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                      data-testid="staff-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">{t("phone")}</label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+39 123 456 7890"
                      data-testid="staff-phone-input"
                    />
                  </div>
                </div>

                {/* ── SERVICES MULTI-SELECT ─────────────────────── */}
                <div className="rounded-2xl border-2 border-dashed border-blue-200 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-950/15 p-3.5">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-600" />
                      <label className="text-sm font-bold text-slate-800 dark:text-slate-100">
                        {language === "it" ? "Servizi eseguibili" : "Services performed"}
                      </label>
                    </div>
                    <span className="text-[10px] font-mono font-bold text-slate-500">
                      {formData.service_ids.length} / {services.length}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2.5 leading-snug">
                    {language === "it"
                      ? "Seleziona quali servizi questo operatore può eseguire. Attiva il filtro intelligente nel calendario e nel booking pubblico."
                      : "Select which services this staff member can perform. Enables the smart filter in the calendar and public booking."}
                  </p>
                  {services.length === 0 ? (
                    <div className="flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg px-3 py-2">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        {language === "it"
                          ? "Nessun servizio configurato · crea i tuoi servizi nella pagina 'Servizi'."
                          : "No services configured · create your services first in the 'Services' page."}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <button type="button" onClick={selectAllServices}
                                className="text-[10px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-2 py-1 rounded-full"
                                data-testid="staff-services-select-all">
                          {language === "it" ? "Tutti" : "All"}
                        </button>
                        <button type="button" onClick={clearAllServices}
                                className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-white/5 px-2 py-1 rounded-full"
                                data-testid="staff-services-clear-all">
                          {language === "it" ? "Nessuno" : "None"}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1">
                        {services.map(svc => {
                          const checked = formData.service_ids.includes(svc.service_id);
                          return (
                            <button
                              key={svc.service_id}
                              type="button"
                              onClick={() => toggleService(svc.service_id)}
                              className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left transition-all ${
                                checked
                                  ? "bg-blue-500 text-white border-blue-500 shadow-sm"
                                  : "bg-white dark:bg-slate-900/40 border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:border-blue-300"
                              }`}
                              data-testid={`staff-service-toggle-${svc.service_id}`}
                            >
                              <span className={`w-4 h-4 rounded-md border-2 shrink-0 flex items-center justify-center ${checked ? "bg-white/20 border-white" : "border-slate-300 dark:border-white/20"}`}>
                                {checked && <Check className="w-3 h-3" />}
                              </span>
                              <span className="text-[11px] font-bold leading-tight truncate flex-1">
                                {svc.name}
                              </span>
                              {svc.duration && (
                                <span className={`text-[9px] font-mono ${checked ? "opacity-90" : "text-slate-400"}`}>
                                  {svc.duration}m
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm">
                    {language === "it" ? "Operatore attivo" : "Staff active"}
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("cancel")}
                  </Button>
                  <Button type="submit" data-testid="save-staff-btn">
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Staff Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((member) => {
            const memberServices = (member.service_ids || []).map(id => servicesById[id]).filter(Boolean);
            const unconfigured = services.length > 0 && memberServices.length === 0;
            return (
              <div
                key={member.staff_id}
                className={`p-5 rounded-2xl border bg-card shadow-card card-hover ${
                  !member.is_active ? "opacity-60" : ""
                }`}
                data-testid={`staff-card-${member.staff_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                      <span className="font-semibold text-accent text-lg">{member.name[0]}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{member.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        member.is_active ? "badge-success" : "badge-error"
                      }`}>
                        {member.is_active
                          ? (language === "it" ? "Attivo" : "Active")
                          : (language === "it" ? "Inattivo" : "Inactive")
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {member.email && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {member.email}
                    </p>
                  )}
                  {member.phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {member.phone}
                    </p>
                  )}
                </div>

                {/* Services pills */}
                {services.length > 0 && (
                  <div className="border-t pt-3 mb-3" data-testid={`staff-card-services-${member.staff_id}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Briefcase className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        {language === "it" ? "Servizi" : "Services"} · {memberServices.length}
                      </span>
                      {unconfigured && (
                        <span className="ml-auto inline-flex items-center text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                          {language === "it" ? "Da config." : "Configure"}
                        </span>
                      )}
                    </div>
                    {memberServices.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {memberServices.slice(0, 6).map(svc => (
                          <span key={svc.service_id}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/40">
                            {svc.name}
                          </span>
                        ))}
                        {memberServices.length > 6 && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500">
                            +{memberServices.length - 6}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400">
                        {language === "it" ? "Nessun servizio assegnato" : "No services assigned"}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end gap-1 pt-3 border-t">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(member)}
                    data-testid={`edit-staff-${member.staff_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(member.staff_id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-staff-${member.staff_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {staff.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "it" ? "Nessun operatore. Aggiungi il primo!" : "No staff. Add the first one!"}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Staff;
